import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionLabel, Marquee, EASE } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";

const HERO = "/media/poster-accueil.jpg";
const QUALIOPI_LOGO = "/media/logo-qualiopi.jpg";
const TF1_LOGO = "/media/logo-tf1.jpg";
const BANNIERE = "/media/banniere-praticien.webp";
const MAGIE = "/media/magie-eau.jpg";

function Hero() {
  const { tr } = useLang();
  const { scrollY } = useScroll();
  const fade = useTransform(scrollY, [0, 350], [1, 0]);

  return (
    <section className="relative h-[100svh] min-h-[660px] overflow-hidden bg-abyss" data-testid="hero">
      <div className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={HERO}
          data-testid="hero-video"
          className="h-full w-full object-contain scale-[0.95] md:scale-[0.82]"
        >
          <source src="/media/video-accueil.mp4" type="video/mp4" />
        </video>
      </div>
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(3,10,22,0.5)_0%,rgba(3,10,22,0.22)_45%,rgba(3,10,22,0.05)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(3,10,22,0.35)_0%,rgba(3,10,22,0.05)_45%,rgba(3,10,22,0.4)_100%)]" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-24 md:px-10 md:pb-20"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
        >
          <h1 data-testid="hero-title" className="font-serif font-light leading-[1.08] text-white [text-shadow:0_2px_24px_rgba(3,10,22,0.9)]">
            <span className="block text-4xl md:text-6xl">{tr("L'école française")}</span>
            <span className="mt-2 block text-5xl md:text-8xl italic text-aqua [text-shadow:0_2px_24px_rgba(3,10,22,0.9)]">{tr("de Janzu")}</span>
          </h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.9, ease: EASE }}
          className="mt-7 max-w-2xl text-lg md:text-2xl leading-relaxed text-foam/90 [text-shadow:0_1px_22px_rgba(3,10,22,0.85),0_0_10px_rgba(3,10,22,0.55)]"
        >
          {tr("Voyagez dans une eau chauffée à 33 °C avec le Janzu, un soin aquatique qui se reçoit et se transmet. Notre école vous forme, du premier module au master pro, au sein de trois magnifiques destinations océanes.")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.9, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/formations"
            data-testid="hero-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-aqua px-8 py-4 text-base font-semibold text-abyss transition-all duration-300 hover:bg-foam hover:shadow-[0_0_36px_rgba(56,189,248,0.5)]"
          >
            {tr("Réserver une formation")}
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/philosophie"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center gap-2 rounded-full border border-foam/25 px-8 py-4 text-base font-medium text-foam/85 transition-colors duration-300 hover:border-foam/60 hover:text-foam"
          >
            {tr("Découvrir la pratique")}
          </Link>
        </motion.div>

      </motion.div>
    </section>
  );
}

function QualiopiBadge() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.5, duration: 0.8, ease: EASE }}
      className="fixed bottom-4 right-4 z-40"
      data-testid="qualiopi-badge"
    >
      <Link
        to="/certification"
        className="block transition-transform duration-300 hover:scale-105"
      >
        <img src={QUALIOPI_LOGO} alt="Certification Qualiopi" className="h-12 w-auto rounded-lg shadow-[0_10px_36px_rgba(3,10,22,0.55)]" />
      </Link>
    </motion.div>
  );
}

function TF1Badge() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.7, duration: 0.8, ease: EASE }}
      className="hidden md:block fixed bottom-4 left-4 z-40"
      data-testid="tf1-badge"
    >
      <Link
        to="/presse"
        className="block transition-transform duration-300 hover:scale-105"
      >
        <img src={TF1_LOGO} alt="Reportage TF1 — Grands Reportages" className="h-12 w-auto rounded-lg shadow-[0_10px_36px_rgba(3,10,22,0.55)]" />
      </Link>
    </motion.div>
  );
}

export default function Landing() {
  const { tr } = useLang();
  return (
    <main className="relative">
      <Hero />
      <Marquee
        items={["Flottaison", "Respiration", "Silence", "Lâcher-prise", "Eau à 33 °C", "Janzu®"].map(tr)}
      />

      <section className="relative overflow-hidden" data-testid="magie-banner">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-10 md:py-28">
          <Reveal>
            <div className="img-clip-b overflow-hidden">
              <img
                src={MAGIE}
                alt="OJANZU — La magie de l'eau : flottaison dans un bassin naturel au cœur de la végétation"
                data-testid="magie-eau-banner"
                loading="lazy"
                className="h-[420px] w-full object-cover [object-position:center_bottom] md:h-[700px]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative overflow-hidden" data-testid="final-cta-section">
        <div className="mx-auto max-w-7xl px-5 pb-24 md:px-10 md:pb-32">
          <Reveal>
            <div className="overflow-hidden rounded-[2.5rem] bg-[linear-gradient(180deg,#bfe7f4_0%,#e8f7fb_50%,#c8ebf6_100%)] px-6 py-14 text-center shadow-[0_40px_120px_rgba(56,189,248,0.15)] md:py-20">
              <img
                src={BANNIERE}
                alt="Devenez Praticien Janzu® — Formation dispensée toute l'année par l'École française de Janzu"
                data-testid="banniere-praticien"
                loading="lazy"
                className="mx-auto w-full max-w-md rounded-3xl shadow-[0_30px_80px_rgba(3,10,22,0.3)]"
              />
              <Link
                to="/formations"
                data-testid="final-cta-button"
                className="group mt-10 inline-flex items-center gap-2 rounded-full bg-abyss px-10 py-4 text-base font-semibold text-foam transition-all duration-300 hover:bg-aqua hover:text-abyss hover:shadow-[0_0_40px_rgba(56,189,248,0.55)]"
              >
                {tr("Réserver ma place")}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <QualiopiBadge />
      <TF1Badge />
    </main>
  );
}
